import { type TypedDocumentNode, gql } from '@apollo/client';
import { PropsWithChildren, ReactNode } from 'react';
import styled, { CSSProperties, keyframes } from 'styled-components';

import { FireBackground } from '@sorare/core/src/atoms/animations/FireBackground';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';

import type { CardLayout_so5lineup } from './__generated__/index.graphql';

export const DELAY_BEFORE_ANIMATION_MS = 3100;

const cardEnter = keyframes`
  from {
    opacity: 0.2;
    transform: translateX(-10%) translateY(20%) rotateY(-70deg);
  }
  to {
    opacity: 1;
    transform: translateX(0%) translateY(0%) rotateY(0deg);
  }
`;
const infoEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(30%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;
const Root = styled.div`
  --border-color: var(--c-score-high);
  --bg-rgb-color: 34, 252, 95;

  position: relative;
  isolation: isolate;
  aspect-ratio: 210 / 340;
  border: 4px solid transparent;
  border-radius: var(--double-and-a-half-unit);
  opacity: 0;
  overflow: hidden;
  height: 100%;

  background:
    linear-gradient(
        180deg,
        rgba(var(--bg-rgb-color), 0.2),
        rgba(var(--bg-rgb-color), 0) 50%
      )
      padding-box,
    linear-gradient(180deg, var(--c-black), var(--c-black) 50%) padding-box,
    var(--border-color) border-box;

  animation: 0.5s ${cardEnter} ease-in-out var(--delay) forwards;
`;
const InnerWrapper = styled(Vertical).attrs({ gap: 3 })`
  padding-top: var(--double-unit);
  height: 100%;

  background: radial-gradient(
      60% 27% at 50% 106.3%,
      rgba(var(--bg-rgb-color), 0.5) 0%,
      rgba(var(--bg-rgb-color), 0) 100%
    ),
    linear-gradient(180deg, transparent 20%, var(--c-black) 70%),
    var(--picture-url-bg);
`;
const FullHeightVertical = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
  height: 100%;
  flex: 1;
  justify-content: space-between;
`;
const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  flex: 1;
`;
const TopLeftIcon = styled.div`
  padding: 0 var(--double-unit);
  align-self: flex-start;
`;
const CenteredIconWrapper = styled(Horizontal).attrs({ center: true })`
  flex: 1;
`;
const SeasonalityLabelWrapper = styled.div`
  opacity: 0;
  animation: ${infoEnter} 0.4s ease-out calc(var(--delay) + 550ms) forwards;
`;
const Title = styled(BodyM)`
  text-align: center;
  opacity: 0;
  animation: ${infoEnter} 0.4s ease-out calc(var(--delay) + 500ms) forwards;
`;
const SizedFireBackground = styled(FireBackground)`
  opacity: 0.7;
  position: absolute;
  z-index: -1;
  height: 100%;
  left: -20%;
  right: -20%;
  bottom: 0;
`;

const findAppearanceWithBestScore = (
  appearances?: CardLayout_so5lineup['so5Appearances']
): CardLayout_so5lineup['so5Appearances'][number] | undefined => {
  return appearances?.reduce(
    (acc, cur) =>
      (cur.so5Score?.score || 0) > (acc.so5Score?.score || 0) ? cur : acc,
    appearances[0]
  );
};

type Props = {
  delay: number;
  so5Lineup: CardLayout_so5lineup | null;
  topLeftIcon?: ReactNode;
  icon?: ReactNode;
  subtitle?: ReactNode;
  title: ReactNode;
  stats?: ReactNode;
  footer: ReactNode;
  borderColor: string;
  bgRgbColor: string;
  withFire?: boolean;
};
export const CardLayout = ({
  delay,
  so5Lineup,
  topLeftIcon,
  icon,
  subtitle,
  title,
  stats,
  footer,
  borderColor,
  bgRgbColor,
  children,
  withFire,
}: PropsWithChildren<Props>) => {
  const bestAppearance = findAppearanceWithBestScore(so5Lineup?.so5Appearances);
  return (
    <Root
      style={
        {
          '--border-color': borderColor,
          '--bg-rgb-color': bgRgbColor,
          '--delay': `${delay}ms`,
        } as CSSProperties
      }
    >
      {withFire && <SizedFireBackground />}
      <InnerWrapper
        style={
          {
            zIndex: withFire ? 1 : 0,
            '--picture-url-bg': bestAppearance
              ? `url(${bestAppearance?.player.pictureUrl}) no-repeat 0 30px / cover`
              : `linear-gradient(to bottom, transparent, transparent)`,
          } as CSSProperties
        }
      >
        <TopLeftIcon>{topLeftIcon}</TopLeftIcon>
        <CenteredIconWrapper>{icon}</CenteredIconWrapper>
        <FullHeightVertical>
          <Wrapper>
            <SeasonalityLabelWrapper>
              <BodyS as="p">{subtitle}</BodyS>
            </SeasonalityLabelWrapper>
            <Title bold>{title}</Title>
            {children}
          </Wrapper>
          {stats}
          {footer}
        </FullHeightVertical>
      </InnerWrapper>
    </Root>
  );
};

CardLayout.fragments = {
  so5Lineup: gql`
    fragment CardLayout_so5lineup on So5Lineup {
      id
      so5Appearances {
        id
        player {
          slug
          pictureUrl(derivative: "squared")
        }
        so5Score {
          id
          score
        }
      }
    }
  ` as TypedDocumentNode<CardLayout_so5lineup>,
};
